/**
 * Segment Event Debugger
 *
 * Intercepts and logs all Segment events for debugging purposes.
 * Only enabled when isSessionReplayDebugModeEnabled is true.
 *
 * Usage:
 *   import { initSegmentDebugger } from '@edulastic/api/src/segmentDebugger'
 *   initSegmentDebugger(window.analytics, debugMode)
 *
 *   // Then use:
 *   window.segmentDebugger.getEvents()
 *   window.segmentDebugger.summary()
 */

export const initSegmentDebugger = (analytics, debugMode = false) => {
  if (!debugMode || !analytics) return

  window.segmentDebugger = {
    events: [],
    enabled: true,

    install() {
      const self = this

      try {
        // Intercept track calls
        const wrappedTrack = analytics.track
        analytics.track = function debugTrack(...args) {
          if (self.enabled) {
            const [event, properties] = args
            const eventData = {
              type: 'track',
              event,
              properties,
              sessionReplayId: properties?.['[Amplitude] Session Replay ID'],
              timestamp: new Date().toISOString(),
            }

            self.events.push(eventData)
            console.log('[Session Replay] 📊 Track:', event, {
              hasSessionReplayId: !!eventData.sessionReplayId,
              sessionReplayId: eventData.sessionReplayId,
              properties,
            })
          }
          return wrappedTrack.apply(this, args)
        }

        // Intercept identify calls
        const wrappedIdentify = analytics.identify
        analytics.identify = function debugIdentify(...args) {
          if (self.enabled) {
            const [userId, traits] = args
            const eventData = {
              type: 'identify',
              userId,
              traits,
              timestamp: new Date().toISOString(),
            }

            self.events.push(eventData)
            console.log('[Session Replay] 👤 Identify:', userId, traits)
          }
          return wrappedIdentify.apply(this, args)
        }

        // Intercept page calls
        const wrappedPage = analytics.page
        analytics.page = function debugPage(...args) {
          if (self.enabled) {
            const [category, name, properties] = args
            const eventData = {
              type: 'page',
              category,
              name,
              properties,
              sessionReplayId: properties?.['[Amplitude] Session Replay ID'],
              timestamp: new Date().toISOString(),
            }

            self.events.push(eventData)
            console.log('[Session Replay] 📄 Page:', {
              category,
              name,
              properties,
            })
          }
          return wrappedPage.apply(this, args)
        }

        console.log('[Session Replay] 🐛 Segment debugger installed')
        console.log('[Session Replay]    Access via: window.segmentDebugger')
      } catch (error) {
        console.error(
          '[Session Replay] ⚠️ Failed to install debugger:',
          error.message
        )
        console.error('[Session Replay] Debugger features will be limited')
      }
    },

    /**
     * Get all captured events
     */
    getEvents() {
      return this.events
    },

    /**
     * Get only events with Session Replay ID
     */
    getEventsWithReplay() {
      return this.events.filter((e) => e.sessionReplayId)
    },

    /**
     * Get only track events
     */
    getTrackEvents() {
      return this.events.filter((e) => e.type === 'track')
    },

    /**
     * Find events by name (partial match)
     */
    findEvent(eventName) {
      return this.events.filter((e) =>
        e.event?.toLowerCase().includes(eventName.toLowerCase())
      )
    },

    /**
     * Get the last tracked event
     */
    getLastEvent() {
      return this.events[this.events.length - 1]
    },

    /**
     * Clear all captured events
     */
    clear() {
      this.events = []
      console.log('[Session Replay] Event log cleared')
    },

    /**
     * Export events to JSON
     */
    export() {
      const data = JSON.stringify(this.events, null, 2)
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `segment-events-${Date.now()}.json`
      a.click()
      URL.revokeObjectURL(url)
      console.log('[Session Replay] ✅ Events exported to JSON file')
    },

    /**
     * Print a summary of captured events
     */
    summary() {
      const total = this.events.length
      const trackEvents = this.events.filter((e) => e.type === 'track').length
      const withReplay = this.events.filter((e) => e.sessionReplayId).length
      const identify = this.events.filter((e) => e.type === 'identify').length
      const page = this.events.filter((e) => e.type === 'page').length

      const summary = {
        'Total Events': total,
        'Track Events': trackEvents,
        'Identify Events': identify,
        'Page Events': page,
        'With Session Replay ID': withReplay,
        'Without Session Replay ID': total - withReplay,
        'Replay Coverage':
          total > 0 ? `${((withReplay / total) * 100).toFixed(1)}%` : '0%',
      }

      console.group('📊 Segment Events Summary')
      console.table(summary)
      console.groupEnd()

      return summary
    },

    /**
     * Verify that all events have Session Replay ID
     */
    verify() {
      const trackEvents = this.getTrackEvents()
      const eventsWithReplay = trackEvents.filter((e) => e.sessionReplayId)
      const eventsWithoutReplay = trackEvents.filter((e) => !e.sessionReplayId)

      console.group('🔍 Session Replay Verification')
      console.log(`Total track events: ${trackEvents.length}`)
      console.log(`✅ With Session Replay ID: ${eventsWithReplay.length}`)

      if (eventsWithoutReplay.length > 0) {
        console.warn(
          `❌ Without Session Replay ID: ${eventsWithoutReplay.length}`
        )
        console.log('Events missing Session Replay ID:')
        eventsWithoutReplay.forEach((e) => {
          console.log(`  - ${e.event} (${e.timestamp})`)
        })
      } else {
        console.log('✅ All events have Session Replay ID!')
      }

      console.groupEnd()

      return {
        total: trackEvents.length,
        withReplay: eventsWithReplay.length,
        withoutReplay: eventsWithoutReplay.length,
        success: eventsWithoutReplay.length === 0,
      }
    },

    /**
     * Enable/disable event capture
     */
    toggle(enabled) {
      this.enabled = enabled
      console.log(
        `[Session Replay] Debugger ${enabled ? 'enabled' : 'disabled'}`
      )
    },
  }

  window.segmentDebugger.install()
}

export default {
  initSegmentDebugger,
}
